// theme-sources/js/core-ui/components/ziggy-trigger.js

import { sectionLogger } from "./_logger";

const DEBUG = true;
const logger = sectionLogger("ZiggyTrigger", DEBUG);

class ZiggyTrigger extends HTMLElement {
  connectedCallback() {
    this.target = this.getAttribute("target");
    this.action = this.getAttribute("action") || "toggle";

    if (!this.target) return;

    // Accessibilité (sans écraser si déjà défini)
    if (!this.hasAttribute("role")) {
      this.setAttribute("role", "button");
    }

    if (!this.hasAttribute("tabindex")) {
      this.setAttribute("tabindex", "0");
    }

    // Bind methods
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onOpen = this.onOpen.bind(this);
    this.onClose = this.onClose.bind(this);

    this.addEventListener("click", this.onClick);
    this.addEventListener("keydown", this.onKeydown);

    // état initial
    if (!this.hasAttribute("aria-expanded")) {
      this.setAttribute("aria-expanded", "false");
    }

    this._bound = false; // évite double binding sur la target
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
    this.removeEventListener("keydown", this.onKeydown);

    const targetEl = this.resolveTarget();
    if (targetEl && this._bound) {
      targetEl.removeEventListener("drawer:open", this.onOpen);
      targetEl.removeEventListener("drawer:close", this.onClose);
      targetEl.removeEventListener("modal:open", this.onOpen);
      targetEl.removeEventListener("modal:close", this.onClose);
    }

    this._bound = false;
  }

  onKeydown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.click();
    }
  }

  onOpen() {
    this.setAttribute("aria-expanded", "true");
  }

  onClose() {
    this.setAttribute("aria-expanded", "false");
  }

  resolveTarget() {
    const raw = this.target?.trim();
    if (!raw) return null;

    const id = raw.startsWith("#") ? raw.slice(1) : raw;

    return (
      document.getElementById(id) ||
      document.querySelector(
        `ziggy-drawer[name="${raw}"], ziggy-modal[name="${raw}"]`,
      )
    );
  }

  bindTargetEvents(targetEl) {
    if (this._bound) return;

    if (targetEl.id) {
      this.setAttribute("aria-controls", targetEl.id);
    }

    targetEl.addEventListener("drawer:open", this.onOpen);
    targetEl.addEventListener("drawer:close", this.onClose);
    targetEl.addEventListener("modal:open", this.onOpen);
    targetEl.addEventListener("modal:close", this.onClose);

    this._bound = true;
  }

  onClick(event) {
    event.preventDefault();

    const targetEl = this.resolveTarget();

    if (!targetEl) {
      logger.warn("ZiggyTrigger target not found", this.target);
      return;
    }

    // On bind les events seulement quand la target existe vraiment
    this.bindTargetEvents(targetEl);

    logger.info("dispatch action", {
      action: this.action,
      target: this.target,
      resolved: targetEl,
    });

    if (typeof targetEl[this.action] === "function") {
      targetEl[this.action]();
    } else if (this.action === "open") {
      targetEl.setAttribute("open", "");
    } else if (this.action === "close") {
      targetEl.removeAttribute("open");
    } else if (this.action === "toggle") {
      targetEl.toggleAttribute("open");
    } else {
      logger.warn(`action "${this.action}" not supported`, targetEl);
    }
  }
}

if (!customElements.get("ziggy-trigger")) {
  customElements.define("ziggy-trigger", ZiggyTrigger);
}
